import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useOnClickOutside } from 'lib/utils';
import { Modal, LoadingIndicator, Search } from 'lib/components';
import { VideoListItem } from 'lib/api/types';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import { BsCalendarEvent } from 'react-icons/bs';
import { getVideos } from 'lib/api';
import { DEFAULT_FOLDER } from 'lib/const/Folders';
import { Button } from 'react-covideo-common';
import { SubHeading } from 'lib/components/styles/typography';
import { Flex } from 'lib/components/styles/layout';
import { FolderV2Dropdown } from 'lib/components/dropdown/folders/FolderV2Dropdown';
import { FolderOption } from 'lib/components/dropdown/folders/customOption';
import { getFolderName } from 'lib/utils/folders';
import { useAuth } from 'lib/context';

const CloseButtonWrap = styled.div`
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;
const Menu = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const DropdownContainer = styled.div`
  width: 254px;
  .border-radius > div {
    border-radius: 5px;
  }
`;

const TooltipWrapper = styled.div`
  position: relative;
  height: 24px;
  bottom: 10px;
`;

const TooltipContent = styled.div`
  z-index: 99999;
  position: absolute;
  width: 100%;
  height: auto;
  display: inline-flex;
  padding: 4px 12px 6px 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  right: 0%;
  top: 150%;
  left: -8%;

  border-radius: 1px;
  background: var(--white-100, #fff);

  background-color: ${({ theme }) => theme.colors.neutral[10]};
  box-shadow: 0 8px 32px 0 rgba(29, 30, 36, 0.2);
  font-size: 14px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out;
`;

const TooltipTrigger = styled.div`
  display: inline-block;
  width: 100%;
  cursor: pointer;

  &:hover + ${TooltipContent} {
    opacity: 1;
    visibility: visible;
  }
`;

type ThumbnailProps = {
  image: string;
};
const VideoList = styled.div`
  height: 350px;
  padding-bottom: 10px;
  overflow-y: auto;
`;

const VideoItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 100%;
  background: ${({ theme }) => theme.colors.white[100]};
  border: 1px solid ${({ theme }) => theme.colors.neutral[10]};
  box-shadow: 0px 4px 12px rgba(29, 30, 36, 0.04);
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  &:hover {
    border: 1px solid ${({ theme }) => theme.colors.neutral[40]};
    button {
      border: 1px solid ${({ theme }) => theme.colors.neutral[40]};
    }
  }
`;
const VideoItemThumbnail = styled.div<ThumbnailProps>`
  background-image: url(${props => props.image});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-color: ${({ theme }) => theme.colors.white[100]};
  width: 96px;
  height: 64px;
  margin-right: 24px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
`;
const VideoItemTitle = styled.h3`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 268px;
  margin: 8px 0 4px 0;
`;
const VideoItemDetails = styled.div`
  color: ${({ theme }) => theme.colors.black[100]};
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  span {
    margin-left: 10px;
  }
`;
const VideoItemButtonWrapper = styled.div`
  margin-right: 12px;
`;
const EmptyList = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  max-width: 100%;
`;

type Props = {
  handleModalClose: () => void;
  onSelect: (video: any) => void;
};

export const CtaVideoModal = (props: Props) => {
  const theme = useTheme();
  const { handleModalClose, onSelect } = props;
  const { ref } = useOnClickOutside(handleModalClose);
  const [searchQuery, setSearchQuery] = useState('');
  const [videos, setVideos] = useState([] as VideoListItem[]);
  const [loading, setLoading] = useState(true);
  const { userData } = useAuth();
  const [selectedFolderOption, setSelectedFolderOption] =
    useState<FolderOption>({
      label: DEFAULT_FOLDER.name,
      value: DEFAULT_FOLDER.folderId.toString(),
      isSharedFolder: false,
    });

  const onSearch = (query: string) => {
    setSearchQuery(query.toLowerCase());
  };

  const fetchVideos = async () => {
    setLoading(true);
    const folder = getFolderName(selectedFolderOption, userData.id);
    const response = await getVideos({
      page: 0,
      size: searchQuery === '' ? 100 : 999,
      search: searchQuery,
      folder,
    });
    setVideos(response.videos);
    setLoading(false);
  };

  useEffect(() => {
    fetchVideos();
  }, [searchQuery, selectedFolderOption]);

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose();
    }
  };

  useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));
    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  return (
    <Modal style={{ position: 'fixed', width: '750px' }}>
      <div ref={ref} style={{ padding: '32px' }}>
        <Flex flexDirection='row' style={{ marginBottom: '16px' }}>
          <CloseButtonWrap title={'Close'}>
            <AiOutlineArrowLeft
              onClick={() => handleModalClose()}
              color={theme.colors.neutral[40]}
              size={24}
            />
          </CloseButtonWrap>
          <SubHeading style={{ margin: '0 auto' }}>Select video</SubHeading>
        </Flex>
        <Menu>
          <div style={{ width: '258px' }}>
            <Search
              placeholder={`Search videos and tags...`}
              onSearch={onSearch}
              prevSearch={searchQuery}
            />
          </div>
          <div style={{ display: 'flex' }}>
            <DropdownContainer>
              <FolderV2Dropdown
                onChange={option => {
                  setSelectedFolderOption(option);
                }}
                value={selectedFolderOption}
                showAllFolders={true}
                showCreateButton={false}
                dropdownHeight={300}
                containerWidth='254px'
                menuWidth='254px'
                optionWidth='200px'
              />
            </DropdownContainer>
          </div>
        </Menu>
        <VideoList>
          {loading && <LoadingIndicator isLoading={loading} height='200px' />}
          {!loading && videos.length > 0 && (
            <>
              {videos.map((video: any) => (
                <VideoItem key={video.id} onClick={() => onSelect(video)}>
                  <VideoItemThumbnail image={video.thumbnail} />
                  <div>
                    <TooltipWrapper>
                      <TooltipTrigger>
                        <VideoItemTitle>{video.title}</VideoItemTitle>
                      </TooltipTrigger>
                      <TooltipContent>{video.title}</TooltipContent>
                    </TooltipWrapper>
                    <VideoItemDetails>
                      <BsCalendarEvent
                        color={theme.colors.black[40]}
                        size={10}
                      />
                      <span>{video.recordDate}</span>
                    </VideoItemDetails>
                  </div>
                  <VideoItemButtonWrapper>
                    <Button
                      text='Select Video'
                      variant='secondary'
                      size='small'
                    />
                  </VideoItemButtonWrapper>
                </VideoItem>
              ))}
            </>
          )}
          {!loading && !videos.length && (
            <EmptyList>No videos found.</EmptyList>
          )}
        </VideoList>
      </div>
    </Modal>
  );
};
